/**
 * SessionContext — the client mirror of the backend `g.auth` request context.
 *
 * Hydrated once from GET /api/v2/me on app load. Holds identity + the full
 * tenancy scope, plus the CURRENT scope selection (which Org/Site the operator
 * is looking at). RoleGate and every scope-prefixed query key read from here.
 *
 * Zustand (not Context) so scope changes don't re-render the whole tree and so
 * non-React code (query-key builders) can read the current scope synchronously.
 */
import { create } from "zustand";
import type { Me, Org, Role, RoleGrant, Site } from "@/types/domain";
import { ROLE_ORDER } from "@/types/domain";

interface SessionState {
  /** null until GET /api/v2/me resolves. */
  me: Me | null;
  /** Currently selected scope (drives ScopeBar + all query keys). */
  currentOrgId: string | null;
  currentSiteId: string | null;

  /** Hydrate from /me; auto-selects the first org (and its first site). */
  hydrate: (me: Me) => void;
  clear: () => void;
  setOrg: (orgId: string | null) => void;
  setSite: (siteId: string | null) => void;

  /* ---- derived selectors (pure, read current state) ---- */
  currentOrg: () => Org | null;
  currentSite: () => Site | null;
  sitesForCurrentOrg: () => Site[];
  /** Highest role the user holds at the current scope (site falls back to org). */
  effectiveRole: () => Role | null;
  /** True if the user's effective role meets/exceeds `min` at current scope. */
  hasRole: (min: Role) => boolean;
}

/** Rank a role for >= comparisons. */
const rank = (r: Role): number => ROLE_ORDER.indexOf(r);

/** Best (highest) grant matching the given org/site, considering org-wide grants
 *  (site_id === null), platform_admin (crosses orgs) and msp_admin. msp_admin is org-scoped
 *  since 2026-10-08, but /me only lists the customers it holds, so treating it as msp_admin
 *  in every customer the UI can reach is still accurate -- and the server re-checks. */
function bestRole(
  grants: RoleGrant[],
  isMspAdmin: boolean,
  orgId: string | null,
  siteId: string | null,
  isPlatformAdmin = false,
): Role | null {
  if (isPlatformAdmin) return "platform_admin";
  if (isMspAdmin) return "msp_admin";
  if (!orgId) return null;
  let best: Role | null = null;
  for (const g of grants) {
    if (g.org_id !== orgId) continue;
    // An org-wide grant (null site) applies to any site in the org; a
    // site-specific grant only applies when that site is selected.
    if (g.site_id !== null && g.site_id !== siteId) continue;
    if (best === null || rank(g.role) > rank(best)) best = g.role;
  }
  return best;
}

export const useSession = create<SessionState>((set, get) => ({
  me: null,
  currentOrgId: null,
  currentSiteId: null,

  hydrate: (me) => {
    // Preserve the current scope across a RE-hydrate (e.g. after creating a customer
    // or site refreshes /me) instead of snapping back to the first org — otherwise the
    // operator gets bounced out of the customer they are working in. Falls back to the
    // first org/site on the initial login (when there is no current scope yet).
    const prev = get();
    const orgStillValid = !!prev.currentOrgId && me.orgs.some((o) => o.id === prev.currentOrgId);
    const currentOrgId = orgStillValid ? prev.currentOrgId : (me.orgs[0]?.id ?? null);
    const siteStillValid =
      !!prev.currentSiteId &&
      me.sites.some((s) => s.id === prev.currentSiteId && s.org_id === currentOrgId);
    const currentSiteId = siteStillValid
      ? prev.currentSiteId
      : (me.sites.find((s) => s.org_id === currentOrgId)?.id ?? null);
    set({ me, currentOrgId, currentSiteId });
  },

  clear: () => set({ me: null, currentOrgId: null, currentSiteId: null }),

  setOrg: (orgId) => {
    // Switching org resets the site to that org's first (or none).
    const site = get().me?.sites.find((s) => s.org_id === orgId)?.id ?? null;
    set({ currentOrgId: orgId, currentSiteId: site });
  },

  setSite: (siteId) => set({ currentSiteId: siteId }),

  currentOrg: () => {
    const { me, currentOrgId } = get();
    return me?.orgs.find((o) => o.id === currentOrgId) ?? null;
  },

  currentSite: () => {
    const { me, currentSiteId } = get();
    return me?.sites.find((s) => s.id === currentSiteId) ?? null;
  },

  sitesForCurrentOrg: () => {
    const { me, currentOrgId } = get();
    if (!me || !currentOrgId) return [];
    return me.sites.filter((s) => s.org_id === currentOrgId);
  },

  effectiveRole: () => {
    const { me, currentOrgId, currentSiteId } = get();
    if (!me) return null;
    return bestRole(me.roles, me.is_msp_admin, currentOrgId, currentSiteId,
                    me.is_platform_admin === true);
  },

  hasRole: (min) => {
    const eff = get().effectiveRole();
    return eff !== null && rank(eff) >= rank(min);
  },
}));
